<template>
    <section ref="section" class="slice slice-media slice-media--product" data-testid="media-product-slice" aria-label="media">
        <div class="slice-content text-left">
            <div
                ref="innerRef"
                class="slice-media--product__inner"
                :class="{
                    animate: isAnimated,
                    'slice-media--product__inner--reverse': slice.primary.reverse,
                }"
            >
                <div class="slice-media--product__content">
                    <div v-if="slice.primary.tag" class="slice-media--product__tag" data-testid="media-product-tag">
                        <span class="anim-stagger">{{ slice.primary.tag }}</span>
                    </div>
                    <div class="slice-media--product__header">
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.title)"
                            :field="slice.primary.title"
                            class="slice-media--product__title anim-stagger"
                            data-testid="media-product-title"
                        />
                    </div>
                    <div class="slice-media--product__description">
                        <PrismicRichText
                            v-if="isFilled.richText(slice.primary.description)"
                            :field="slice.primary.description"
                            class="anim-stagger"
                            data-testid="media-product-description"
                        />
                    </div>
                    <div v-if="isFilled.link(slice.primary.cta)" class="slice-media--product__cta">
                        <ProductLink
                            :href="slice.primary.cta.url ?? ''"
                            rel="nofollow"
                            class="anim-stagger"
                            :theme="slice.primary.theme ?? 'malt'"
                            data-component-source="product-release-slice-content-try-ai-button"
                            data-testid="media-product-cta"
                        >
                            {{ slice.primary.cta.text }}
                        </ProductLink>
                    </div>
                </div>
                <div v-if="hasMedia" class="slice-media--product__media" :class="{animate: isAnimated}">
                    <PrismicImage
                        v-if="hasImage"
                        :field="slice.primary.image"
                        class="slice-media--product__media__element anim-stagger"
                        data-testid="media-product-image"
                    />
                    <video
                        v-if="hasVideo"
                        class="slice-media--product__media__element anim-stagger"
                        data-testid="media-product-video"
                        autoplay
                        playsinline
                        muted
                        loop
                    >
                        <source :src="videoMp4Url" type="video/mp4" />
                        <source v-if="videoWebMUrl" :src="videoWebMUrl" type="video/webm" />
                    </video>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {MediaSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {useGsap} from '../../../composables/useGsap';
    import ProductLink from '../components/ProductLink.vue';

    type MediaProductInZone = Extract<MediaSlice, {variation: 'mediaProduct'}>;

    const props = defineProps(getSliceComponentProps<MediaProductInZone>());

    const {ScrollTrigger} = useGsap();

    const section = ref<HTMLElement | null>(null);
    const innerRef = ref<HTMLElement | null>(null);
    const isAnimated = ref(false);

    const hasVideo = computed(() => isFilled.linkToMedia(props.slice.primary.videoMp4));
    const hasImage = computed(() => isFilled.image(props.slice.primary.image) && !hasVideo.value);
    const hasMedia = computed(() => hasImage.value || hasVideo.value);
    const videoMp4Url = computed(() => (isFilled.linkToMedia(props.slice.primary.videoMp4) ? props.slice.primary.videoMp4.url : ''));
    const videoWebMUrl = computed(() => (isFilled.linkToMedia(props.slice.primary.videoWebM) ? props.slice.primary.videoWebM.url : ''));

    onMounted(() => {
        const staggerDelay = 0.15;
        const staggers = [...(innerRef.value?.querySelectorAll('.anim-stagger') || [])];
        staggers.forEach((el, index) => {
            (el as HTMLElement).style.transitionDelay = `${index * staggerDelay}s`;
        });

        ScrollTrigger.create({
            trigger: innerRef.value,
            start: 'top bottom',
            onEnter: () => (isAnimated.value = true),
            onLeaveBack: () => (isAnimated.value = false),
        });
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-media--product {
        .slice-content {
            @include mq.screen_lg(min) {
                padding-top: 120px;
                padding-bottom: 120px;
            }
        }

        &__inner {
            position: relative;
            text-align: left;
            display: flex;
            flex-direction: column-reverse;
            gap: var(--joy-core-spacing-8);

            @include mq.screen_sm(min) {
                flex-direction: row;
                align-items: center;
                gap: 100px;

                &--reverse {
                    flex-direction: row-reverse;
                }
            }
        }

        &__media {
            flex: none;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            max-width: 400px;
            margin: 0 auto;
            padding: 0 4px 4px;

            @include mq.screen_sm(min) {
                max-width: none;
                width: 50%;
            }

            &__element {
                box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
                width: 100%;
                border-radius: var(--joy-core-radius-4);
            }
        }

        &__tag {
            overflow: hidden;
            margin-bottom: 19px;

            span {
                display: inline-block;
                background-color: var(--joy-color-neutral-20);
                border-radius: var(--joy-core-radius-2);
                padding: 6px var(--joy-core-spacing-3);
                font-size: var(--joy-font-size-primary-200);
                font-weight: var(--joy-font-weight-bold);
                letter-spacing: var(--joy-font-letter-spacing-tight);
                color: var(--joy-color-neutral-40);
                font-family: var(--joy-font-family-base);
                text-transform: uppercase;
                user-select: none;
            }
        }

        &__header {
            overflow: hidden;
        }

        &__title {
            transform: translate3d(0, 100%, 0);
            padding-bottom: var(--joy-core-spacing-4);

            h2 {
                letter-spacing: -0.4px;
                line-height: var(--joy-line-height-small);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                color: var(--joy-color-text-heading);

                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-secondary-400);
                }
                @include mq.screen_lg(min) {
                    font-size: var(--joy-font-size-secondary-600);
                }
            }
        }

        &__description {
            font-size: var(--joy-font-size-primary-400);
            line-height: var(--joy-line-height-large);
            overflow: hidden;

            @include mq.screen_sm(min) {
                font-size: var(--joy-font-size-primary-600);
            }

            p:not(:first-child) {
                margin-top: var(--joy-core-spacing-5);
            }
        }

        &__cta {
            margin-top: 27px;
            overflow: hidden;
        }

        &__media__element {
            transition-delay: 0s !important;
            transition-duration: 1s !important;
        }

        .anim-stagger {
            transform: translate3d(0, 100%, 0);
            will-change: transform;
            backface-visibility: hidden;
            transition: none;
        }

        .animate {
            .anim-stagger {
                transition: transform var(--ai-transition-duration) var(--ai-transition-timing-function);
                transform: translate3d(0, 0, 0);
            }
        }
    }
</style>
